import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Steps,
  Select,
  Modal,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  SendOutlined,
  DeliveredProcedureOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useStores } from '../../../stores';
import './PartnerOrders.scss';

const { Title, Text } = Typography;
const { Option } = Select;
const { Step } = Steps;

const PartnerOrderDetail = observer(() => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { partnerOrderStore } = useStores();
  const { currentOrder, loading, error } = partnerOrderStore;
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    partnerOrderStore.fetchOrderById(id);
  }, [partnerOrderStore, id]);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      PROCESSING: 'cyan',
      SHIPPED: 'purple',
      DELIVERED: 'green',
      CANCELLED: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusStep = (status) => {
    const steps = {
      PENDING: 0,
      CONFIRMED: 1,
      PROCESSING: 2,
      SHIPPED: 3,
      DELIVERED: 4,
      CANCELLED: -1
    };
    return steps[status] || 0;
  };

  const getStatusText = (status) => {
    const statusMap = {
      PENDING: t('partner.orders.status.pending'),
      CONFIRMED: t('partner.orders.status.confirmed'),
      PROCESSING: t('partner.orders.status.processing'),
      SHIPPED: t('partner.orders.status.shipped'),
      DELIVERED: t('partner.orders.status.delivered'),
      CANCELLED: t('partner.orders.status.cancelled')
    };
    return statusMap[status] || status;
  };

  const handleStatusUpdate = async (newStatus) => {
    Modal.confirm({
      title: t('partner.orders.updateStatusTitle', { status: getStatusText(newStatus) }),
      content: t('partner.orders.updateStatusContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setUpdating(true);
        const result = await partnerOrderStore.updateOrderStatus(id, newStatus);
        setUpdating(false);

        if (result.success) {
          message.success(t('partner.orders.messages.statusUpdated', { status: getStatusText(newStatus) }));
        } else {
          message.error(result.error || t('partner.orders.messages.updateFailed'));
        }
      }
    });
  };

  const getNextStatusAction = (currentStatus) => {
    const actions = {
      PENDING: {
        status: 'CONFIRMED',
        label: t('partner.orders.actions.confirm'),
        icon: <CheckOutlined />,
        type: 'primary'
      },
      CONFIRMED: {
        status: 'PROCESSING',
        label: t('partner.orders.actions.startProcessing'),
        icon: <CheckOutlined />,
        type: 'primary'
      },
      PROCESSING: {
        status: 'SHIPPED',
        label: t('partner.orders.actions.markShipped'),
        icon: <SendOutlined />,
        type: 'primary'
      },
      SHIPPED: {
        status: 'DELIVERED',
        label: t('partner.orders.actions.markDelivered'),
        icon: <DeliveredProcedureOutlined />,
        type: 'primary'
      }
    };
    return actions[currentStatus];
  };

  const columns = [
    {
      title: t('partner.orders.tableHeaders.product'),
      key: 'product',
      render: (_, record) => (
        <div>
          <div><strong>{record.product_name}</strong></div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {t('partner.products.sku')}: {record.sku}
            {record.color && ` | ${t('partner.products.color')}: ${record.color}`}
            {record.size && ` | ${t('partner.products.size')}: ${record.size}`}
          </div>
        </div>
      )
    },
    {
      title: t('partner.orders.tableHeaders.price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (price) => `€${parseFloat(price).toFixed(2)}`
    },
    {
      title: t('partner.orders.tableHeaders.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center'
    },
    {
      title: t('partner.orders.tableHeaders.subtotal'),
      key: 'subtotal',
      width: 120,
      align: 'right',
      render: (_, record) => `€${(parseFloat(record.unit_price) * record.quantity).toFixed(2)}`
    }
  ];

  if (loading && !currentOrder) {
    return (
      <div className="partner-order-detail-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!currentOrder) {
    return (
      <div className="partner-order-detail">
        <Alert
          message={t('partner.orders.orderNotFound')}
          description={t('partner.orders.orderNotFoundDesc')}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const nextAction = getNextStatusAction(currentOrder.status);

  return (
    <div className="partner-order-detail">
      {/* Header */}
      <div className="page-header">
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/partner/orders')}
          >
            {t('partner.orders.backToOrders')}
          </Button>
          <Title level={2}>{t('partner.orders.orderNumber', { id: currentOrder.id })}</Title>
          <Tag color={getStatusColor(currentOrder.status)} style={{ fontSize: 14, padding: '4px 12px' }}>
            {getStatusText(currentOrder.status)}
          </Tag>
        </Space>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Status Timeline */}
      {currentOrder.status !== 'CANCELLED' && (
        <Card style={{ marginBottom: 16 }}>
          <Steps current={getStatusStep(currentOrder.status)} status={currentOrder.status === 'CANCELLED' ? 'error' : 'process'}>
            <Step title={t('partner.orders.status.pending')} description={currentOrder.created_at ? new Date(currentOrder.created_at).toLocaleString() : ''} />
            <Step title={t('partner.orders.status.confirmed')} description={currentOrder.confirmed_at ? new Date(currentOrder.confirmed_at).toLocaleString() : ''} />
            <Step title={t('partner.orders.status.shipped')} description={currentOrder.shipped_at ? new Date(currentOrder.shipped_at).toLocaleString() : ''} />
            <Step title={t('partner.orders.status.delivered')} description={currentOrder.delivered_at ? new Date(currentOrder.delivered_at).toLocaleString() : ''} />
          </Steps>
        </Card>
      )}

      {/* Status Actions */}
      {nextAction && currentOrder.status !== 'DELIVERED' && currentOrder.status !== 'CANCELLED' && (
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type={nextAction.type}
              icon={nextAction.icon}
              loading={updating}
              onClick={() => handleStatusUpdate(nextAction.status)}
            >
              {nextAction.label}
            </Button>
            {currentOrder.status !== 'DELIVERED' && (
              <Button
                danger
                icon={<CloseOutlined />}
                loading={updating}
                onClick={() => handleStatusUpdate('CANCELLED')}
              >
                {t('partner.orders.actions.cancel')}
              </Button>
            )}
          </Space>
        </Card>
      )}

      <Card title={t('partner.orders.orderDetails')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered>
          <Descriptions.Item label={t('partner.orders.orderId')}>#{currentOrder.id}</Descriptions.Item>
          <Descriptions.Item label={t('partner.orders.orderDate')}>
            {new Date(currentOrder.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.status')}>
            <Tag color={getStatusColor(currentOrder.status)}>
              {getStatusText(currentOrder.status)}
            </Tag>
          </Descriptions.Item>

          {/* Customer Info */}
          <Descriptions.Item label={t('partner.orders.customerName')}>
            {currentOrder.user_first_name} {currentOrder.user_last_name}
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.orders.customerEmail')}>
            {currentOrder.user_email}
          </Descriptions.Item>
          <Descriptions.Item label={t('partner.orders.phone')}>
            {currentOrder.user_phone || t('common.na')}
          </Descriptions.Item>

          {/* Shipping Address */}
          <Descriptions.Item label={t('partner.orders.shippingAddress')} span={3}>
            {currentOrder.shipping_address}
          </Descriptions.Item>

          {/* Notes */}
          {currentOrder.notes && (
            <Descriptions.Item label={t('partner.orders.customerNotes')} span={3}>
              {currentOrder.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Order Items */}
      <Card title={t('partner.orders.orderItems')} style={{ marginBottom: 16 }}>
        <Table
          columns={columns}
          dataSource={currentOrder.items}
          rowKey="id"
          pagination={false}
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  <strong>{t('partner.orders.subtotal')}:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>€{parseFloat(currentOrder.subtotal).toFixed(2)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  <strong>{t('partner.orders.shipping')}:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>€{parseFloat(currentOrder.shipping_cost).toFixed(2)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  <Text strong style={{ fontSize: 16 }}>{t('partner.orders.total')}:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                    €{parseFloat(currentOrder.total_amount).toFixed(2)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
        />
      </Card>
    </div>
  );
});

export default PartnerOrderDetail;
